import * as React from 'react';
import './styles.scss';
import classNames from 'classnames';
import {useEffect} from 'react';

export interface ITabData {
    title: string | React.ReactNode;
    content: React.ReactNode;
    mobileTitle?: string | React.ReactNode;
    mobileClassName?: string;
    className?: string;
    contentClassName?: string;
    panelClassName?: string;
    testId?: string;
}

export interface ITabsProps {
    className?: string;
    tabs: {[key: string]: ITabData};
    hasWrapper?: boolean;
    activeTab: string;
    disableHash?: boolean;
    tabsClassName?: string;
    scrollToTop?: boolean;
}

const Tabs: React.FunctionComponent<ITabsProps> = (props) => {
    const [activeTab, setActiveTab] = React.useState(props.activeTab ? props.activeTab : '');
    const [activeTabMobile, setActiveTabMobile] = React.useState('');

    const pointerRef = React.useRef(null);
    const effect = async (e) => {
        if (e.detail && e.detail.tabName) {
            setActiveTab(e.detail.tabName);
            if (pointerRef && pointerRef.current) {
                // @ts-ignore
                pointerRef.current.scrollIntoView(true);
            }
        }
    };
    useEffect(() => {
        window.addEventListener('tab-change', effect);
        return function cleanup () {
            window.removeEventListener('tab-change', effect);
        };
    });

    const handleSelect = (key: string) => {
        if (props.scrollToTop) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        window.dispatchEvent(
            new CustomEvent('tab-selected', {
                detail: {
                    tabKey: key
                }
            })
        );
    };

    const tabs = (
        <ul
            className={classNames('tabbed-content__tabs', props.tabsClassName)}
            role="tablist"
            ref={pointerRef}
        >
            {Object.keys(props.tabs).map((key) => {
                return (
                    <li key={key} className="tabbed-content__tab" role="presentation">
                        <a href={'#'+key} className={activeTab === key ? 'active' : ''} role="tab"
                           aria-controls={key}
                           aria-selected={activeTab === key}
                           onClick={(e) => {
                               e.preventDefault();
                               setActiveTab(key);
                               setActiveTabMobile(key);
                               handleSelect(key);
                               if (!props.disableHash) { window.location.hash = key; }
                           }}
                           data-testid={props.tabs[key].testId}
                        >
                            {props.tabs[key].title}
                        </a>
                    </li>
                );
            })}
        </ul>
    );
    const panels = (
        <div className="tabbed-content__panel-group" role="presentation">
            {Object.keys(props.tabs).map((key) => {
                return (
                    <div key={key} className={classNames('tabbed-content__panel', props.tabs[key].panelClassName, activeTab === key && 'open')} role="tabpanel">
                        <a id={key} className="tabbed-content__panel-anchor"/>
                        <div
                            className={classNames('tabbed-content__panel-heading', activeTab === key && 'open', props.tabs[key].mobileClassName)}
                            role="button"
                            tabIndex={0}
                            aria-pressed="false"
                            aria-controls={key}
                            aria-expanded={activeTab === key}
                            onClick={() => {
                                setActiveTab(key);
                                handleSelect(key);
                                if (activeTabMobile !== key) {
                                    setActiveTabMobile(key);
                                } else {
                                    setActiveTabMobile('');
                                }
                                if (!props.disableHash) {
                                    const timer = setTimeout(() => {
                                        window.location.hash = key;
                                    }, 50);
                                }
                            }}
                        >
                            {props.tabs[key].mobileTitle ? props.tabs[key].mobileTitle : props.tabs[key].title}
                        </div>
                        <div className={classNames('tabbed-content__panel-content', props.tabs[key].contentClassName, activeTabMobile === key && 'mobile-open')} role="region" aria-hidden={activeTab !== key}>
                            {props.tabs[key].content}
                        </div>
                    </div>
                );
            })}
        </div>
    );
    if (props.hasWrapper) {
        return (
            <React.Fragment>
                {tabs}
                {panels}
            </React.Fragment>
        );
    } else {
        return (
            <div className={classNames(props.className, 'tabbed-content' )}>
                {tabs}
                {panels}
            </div>
        );
    }
};

Tabs.displayName = 'Tabs';

export default Tabs;
